import React, { useState, useMemo, useEffect } from "react";
import { COLUMNS } from "./columns";
import {
  FaMinusCircle,
  FaPlusCircle,
  FaTimesCircle,
  FaClock,
  FaCheckCircle
} from "react-icons/fa";
import { ImSpinner6 } from "react-icons/im";
import {
  useTable,
  useRowSelect,
  useFilters,
  useSortBy,
  useExpanded
} from "react-table";
import {
  action,
  createReffPartner,
  monthlyKPIReportsData,
  noDataPresent
} from "../../../../../../../locales/en";
import TableSkeleton from "../../../../../../skeletons/TableSkeleton";
import { useVisibleColumnCount } from "../../../../../../../hooks/useVisibleColumnCount";
import { useDispatch } from "react-redux";
import { getUserKPIGeneratedReportData } from "../../../../../../../redux/userReportsSlice";
import {
  formatDateWithDayjs,
  showToast
} from "../../../../../../../utils/utils";
import TableActionBtnGroup from "../../../../tableactionbtngroup/TableActionBtnGroup";
import privateAxios from "../../../../../../../utils/axiosPrivate";
import { showToastType } from "../../../../../../../utils/constants/appConstants";
import { getKpiGeneratedFileDownloadURL } from "../../../../../../../utils/constants/apiEndPoints";
import "./../../../../reportable/reportable.scss";
import PropTypes from "prop-types";
import { GENERATED_KPI_TOOLTIP_CONFIG } from "../../../../../../../utils/constants/tooltipsConfig";

const GeneratedKPIDownloadTable = (props) => {
  const { refreshTrigger } = props;
  const dispatch = useDispatch();
  const { headerRef, visibleColumnCount } = useVisibleColumnCount();
  const columns = useMemo(() => COLUMNS, []);
  const initialShowSkeleton = true;
  const [kpiData, setKpiData] = useState([]);
  const [expandedRows, setExpandedRows] = useState([]);
  const [showSkeleton, setShowSkeleton] = useState(initialShowSkeleton);
  const [downloadingFile, setDownloadingFile] = useState(null);

  const loadDownloadKpiData = async (silent = false) => {
    try {
      if (!silent) setShowSkeleton(true);
      const response = await dispatch(getUserKPIGeneratedReportData()).unwrap();
      const newData = response?.data || [];
      setKpiData(newData);
      setShowSkeleton(false);
      const hasPending = newData.some((row) => row.dataStatus !== "COMPLETED");
      if (hasPending) {
        setTimeout(() => {
          loadDownloadKpiData(true);
        }, 10000);
      }
    } catch {
      setKpiData([]);
      setShowSkeleton(false);
    }
  };
  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable(
      {
        columns,
        data: kpiData
      },
      useFilters,
      useSortBy,
      useExpanded,
      useRowSelect
    );

  const toggleRow = (rowId) => {
    if (expandedRows.includes(rowId)) {
      setExpandedRows(expandedRows.filter((id) => id !== rowId));
    } else {
      setExpandedRows([...expandedRows, rowId]);
    }
  };

  useEffect(() => {
    loadDownloadKpiData();
  }, [refreshTrigger]);

  const handleFileDownload = async (fileName) => {
    try {
      setDownloadingFile(fileName);
      const response = await privateAxios.get(getKpiGeneratedFileDownloadURL, {
        params: { fileName },
        responseType: "blob"
      });

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${fileName}.xlsx`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      if (error?.response?.data instanceof Blob) {
        const text = await error.response.data.text();
        const json = JSON.parse(text);
        showToast(json.message, showToastType.error);
      } else {
        const message = error?.response?.data?.message || error?.message;
        showToast(message, showToastType.error);
      }
    } finally {
      setDownloadingFile(null);
    }
  };

  return (
    <div className="col-md-12">
      <table
        className={`mt-2 mb-3 downloadkpitable bgless_table`}
        {...getTableProps()}
      >
        <thead ref={headerRef}>
          {headerGroups.map((headerGroup) => {
            const { key, ...rest } = headerGroup.getHeaderGroupProps();
            return (
              <tr key={key} className="parentrow" {...rest}>
                <th />
                {headerGroup.headers.map((column) => (
                  <th key={column.id} {...column.getHeaderProps()}>
                    <span>{column.render("Header")}</span>
                  </th>
                ))}
                <th>{action}</th>
              </tr>
            );
          })}
        </thead>
        <tbody {...getTableBodyProps()}>
          {showSkeleton && (
            <TableSkeleton rows={10} columns={visibleColumnCount} />
          )}
          {!showSkeleton && rows.length === 0 && (
            <tr>
              <td colSpan={columns.length + 2} className="text-center">
                {noDataPresent}
              </td>
            </tr>
          )}
          {!showSkeleton &&
            rows.length > 0 &&
            rows.map((row) => {
              prepareRow(row);
              const isExpanded = expandedRows.includes(row.id);
              return (
                <React.Fragment key={row.id}>
                  <tr className="parentrow" {...row.getRowProps()}>
                    <td className="expand-td">
                      <span
                        className="expandbtn cursor-pointer"
                        onClick={() => toggleRow(row.id)}
                      >
                        {isExpanded ? <FaMinusCircle /> : <FaPlusCircle />}
                      </span>
                    </td>
                    {row.cells.map((cell) => {
                      let content = cell.render("Cell");
                      if (cell.column.id === "fileType") {
                        content = cell.value;
                        if (content) {
                          content = "KPI Report";
                        }
                      }
                      if (cell.column.id === "completedAt") {
                        content = cell.value
                          ? formatDateWithDayjs(cell.value)
                          : monthlyKPIReportsData?.downloadModal?.notGenerated;
                      }

                      if (cell.column.id === "iso2CountryCode") {
                        const country = cell.value;
                        const state = row.original.stateCode;
                        const leadSource = row.original.leadSource;
                        const referralTypes = row.original.referralTypes;
                        const salesLeads = row.original.salesLeads;
                        const withPercent = row.original.withPercent;

                        const parts = [
                          `${createReffPartner?.country}: ${country}`
                        ];
                        if (state) {
                          parts.push(`${createReffPartner?.state}: ${state}`);
                        }
                        if (leadSource) {
                          parts.push(
                            `${monthlyKPIReportsData?.downloadModal?.leadSource}: ${leadSource}`
                          );
                        }
                        if (referralTypes) {
                          parts.push(
                            `${monthlyKPIReportsData?.downloadModal?.referralTypes}: ${referralTypes}`
                          );
                        }
                        if (salesLeads) {
                          parts.push(
                            `${monthlyKPIReportsData?.downloadModal?.salesLeads}: ${salesLeads}`
                          );
                        }
                        if (withPercent) {
                          parts.push(
                            `${monthlyKPIReportsData?.downloadModal?.withPercent}`
                          );
                        }
                        content = parts.join(" | ");
                      }

                      if (cell.column.id === "dataStatus") {
                        const status = cell.value;
                        if (status === "COMPLETED") {
                          content = <FaCheckCircle color="green" />;
                        } else if (status === "PENDING") {
                          content = <FaClock color="orange" />;
                        } else if (status === "IN_PROGRESS") {
                          content = <ImSpinner6 color="dark" />;
                        } else if (status === "FAILED") {
                          content = <FaTimesCircle color="red" />;
                        } else {
                          content = status;
                        }
                      }

                      return (
                        <td
                          key={cell.getCellProps().key}
                          {...cell.getCellProps()}
                        >
                          {content}
                        </td>
                      );
                    })}
                    <td>
                      {row.original.dataStatus !== "COMPLETED" ? null : (
                        <TableActionBtnGroup
                          download={
                            row.original.fileName
                              ? () => handleFileDownload(row.original.fileName)
                              : null
                          }
                          downloadLoading={
                            downloadingFile === row.original.fileName
                          }
                          tooltips={GENERATED_KPI_TOOLTIP_CONFIG}
                        />
                      )}
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr className="childrow colexpand">
                      <td colSpan={columns.length + 2}>
                        <div className="tdbody">
                          {columns.map((column) => {
                            let content = row.original[column.accessor];

                            if (column.accessor === "fileType") {
                              if (content) {
                                content = "KPI Report";
                              }
                            }

                            if (column.accessor === "completedAt") {
                              content = content
                                ? formatDateWithDayjs(content)
                                : monthlyKPIReportsData?.downloadModal
                                    ?.notGenerated;
                            }

                            if (column.accessor === "iso2CountryCode") {
                              const country = row.original.iso2CountryCode;
                              const state = row.original.stateCode;
                              const leadSource = row.original.leadSource;
                              const referralTypes = row.original.referralTypes;
                              const salesLeads = row.original.salesLeads;
                              const withPercent = row.original.withPercent;

                              const parts = [
                                `${createReffPartner?.country}: ${country}`
                              ];
                              if (state) {
                                parts.push(
                                  `${createReffPartner?.state}: ${state}`
                                );
                              }
                              if (leadSource) {
                                parts.push(
                                  `${monthlyKPIReportsData?.downloadModal?.leadSource}: ${leadSource}`
                                );
                              }
                              if (referralTypes) {
                                parts.push(
                                  `${monthlyKPIReportsData?.downloadModal?.referralTypes}: ${referralTypes}`
                                );
                              }
                              if (salesLeads) {
                                parts.push(
                                  `${monthlyKPIReportsData?.downloadModal?.salesLeads}: ${salesLeads}`
                                );
                              }
                              if (withPercent) {
                                parts.push(
                                  `${monthlyKPIReportsData?.downloadModal?.withPercent}`
                                );
                              }
                              content = parts.join(" | ");
                            }

                            if (column.accessor === "dataStatus") {
                              const status = row.original[column.accessor];
                              if (status === "COMPLETED") {
                                content = <FaCheckCircle color="green" />;
                              } else if (status === "PENDING") {
                                content = <FaClock color="orange" />;
                              } else if (status === "IN_PROGRESS") {
                                content = <ImSpinner6 color="dark" />;
                              } else if (status === "FAILED") {
                                content = <FaTimesCircle color="red" />;
                              } else {
                                content = status;
                              }
                            }

                            return (
                              <span
                                key={column.accessor}
                                data-label={column.Header}
                              >
                                {content}
                              </span>
                            );
                          })}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
        </tbody>
      </table>
    </div>
  );
};
GeneratedKPIDownloadTable.propTypes = {
  refreshTrigger: PropTypes.number
};

export default GeneratedKPIDownloadTable;
